import { useState } from 'preact/hooks';
import { DAYS, closureForDate, type DayIndex } from '../../lib/calendar.ts';
import { addDays, frDayDate, frWeekRange } from '../../lib/dates.ts';
import { PERIODS, RITUAL_END, RITUAL_START } from '../../lib/slots.ts';
import { SUBJECTS } from '../../lib/subjects.ts';
import { useStore } from '../../store/store.ts';
import type { SlotIndex } from '../../store/types.ts';
import { closeModal, toast } from '../../ui/ui-store.ts';
import { CompetenceField } from './CompetenceField.tsx';

/**
 * Encode a session once and drop it on several days of the current week.
 */
export function PrepWeekModal() {
  const monday = useStore((s) => s.currentMonday)();

  const [days, setDays] = useState<DayIndex[]>([]);
  const [slot, setSlot] = useState<SlotIndex>(0);
  const [subject, setSubject] = useState('Français');
  const [domain] = useState('');
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [competenceIds, setCompetenceIds] = useState<string[]>([]);
  const [material, setMaterial] = useState('');
  const [differentiation, setDifferentiation] = useState('');

  function toggleDay(day: DayIndex) {
    setDays((current) =>
      current.includes(day) ? current.filter((d) => d !== day) : [...current, day],
    );
  }

  function save() {
    if (!days.length) {
      toast('Choisis au moins un jour');
      return;
    }

    const occupied = days.some((d) => useStore.getState().hasLessonAt(d, slot));
    if (occupied && !confirm('Un ou plusieurs créneaux sont déjà remplis. Les remplacer ?')) return;

    const placed = useStore
      .getState()
      .prepareWeek(days, {
        slot,
        subject,
        domain,
        title,
        detail,
        competenceIds,
        material,
        differentiation,
      });

    closeModal();
    toast(placed ? 'Séance placée dans cette semaine' : 'Aucun jour disponible cette semaine');
  }

  return (
    <>
      <p class="muted">
        {frWeekRange(monday)}. Encode une séance une seule fois et place-la sur plusieurs jours.
      </p>

      <div class="formGrid">
        <div>
          <label for="psub">Matière</label>
          <select id="psub" value={subject} onChange={(e) => setSubject(e.currentTarget.value)}>
            {SUBJECTS.map(([name]) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label for="pslot">Créneau</label>
          <select
            id="pslot"
            value={String(slot)}
            onChange={(e) => setSlot(Number(e.currentTarget.value) as SlotIndex)}
          >
            <option value="-1">
              Rituels {RITUAL_START} – {RITUAL_END}
            </option>
            {PERIODS.map((p) => (
              <option key={p.slot} value={String(p.slot)}>
                {p.start} – {p.end}
              </option>
            ))}
          </select>
        </div>

        <div style={{ gridColumn: '1/-1' }}>
          <label for="ptitle">Titre</label>
          <input
            id="ptitle"
            value={title}
            placeholder="Ex. Lecture guidée"
            lang="fr"
            spellcheck={true}
            autocorrect="on"
            autocapitalize="sentences"
            aria-describedby="prep-spelling-hint"
            onInput={(e) => setTitle(e.currentTarget.value)}
          />
        </div>

        <div style={{ gridColumn: '1/-1' }}>
          <label for="pdetail">Détail / objectif</label>
          <textarea
            id="pdetail"
            value={detail}
            lang="fr"
            spellcheck={true}
            autocorrect="on"
            autocapitalize="sentences"
            aria-describedby="prep-spelling-hint"
            onInput={(e) => setDetail(e.currentTarget.value)}
          />
        </div>

        <div style={{ gridColumn: '1/-1' }}>
          <CompetenceField
            subject={subject}
            slot={slot}
            value={competenceIds}
            onChange={setCompetenceIds}
          />
        </div>

        <div>
          <label for="pmaterial">Matériel</label>
          <input
            id="pmaterial"
            value={material}
            placeholder="Facultatif"
            lang="fr"
            spellcheck={true}
            autocorrect="on"
            autocapitalize="sentences"
            aria-describedby="prep-spelling-hint"
            onInput={(e) => setMaterial(e.currentTarget.value)}
          />
        </div>

        <div style={{ gridColumn: '1/-1' }}>
          <label for="pdiff">Différenciation / élèves</label>
          <input
            id="pdiff"
            value={differentiation}
            placeholder="Facultatif"
            lang="fr"
            spellcheck={true}
            autocorrect="on"
            autocapitalize="sentences"
            aria-describedby="prep-spelling-hint"
            onInput={(e) => setDifferentiation(e.currentTarget.value)}
          />
        </div>

        <p id="prep-spelling-hint" class="spellingHint" style={{ gridColumn: '1/-1' }}>
          Les mots mal orthographiés sont soulignés. Appuie sur un mot (ou fais un clic droit)
          pour afficher les corrections proposées.
        </p>
      </div>

      <div class="detailSection">
        <label>Jours</label>
        <div class="dayChecks">
          {DAYS.map((name, i) => {
            const closure = closureForDate(addDays(monday, i));
            return (
              <label key={name} class="touchChoice">
                <input
                  type="checkbox"
                  checked={days.includes(i as DayIndex)}
                  disabled={closure !== null}
                  onChange={() => toggleDay(i as DayIndex)}
                />
                <span>
                  {name}
                  <br />
                  <span class="small muted">
                    {closure ? closure.label : frDayDate(addDays(monday, i))}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      </div>

      <div style={{ marginTop: '15px' }}>
        <button class="btn primary" onClick={save}>
          Placer dans la semaine
        </button>
      </div>
    </>
  );
}
